import type { MarcaData } from '@/api/laminas';

// La paleta de marcado y la geometría que produce. Vive aparte del visor a
// propósito: el visor solo sabe de TRES gestos (arrastre de dos puntos, trazo
// libre de muchos y vértice a vértice), y acá se decide en qué Feature se
// convierte cada uno. Así agregar una herramienta no toca el manejo de punteros.
//
// F-1 de ARQ-007, medido sobre las dos grabaciones de Procore iOS (20-oct-2026).
// Lo que se replica de su paleta:
//   · submenú de figuras      — Nube de revisión · Cuadrado · Óvalo
//   · submenú de líneas       — Flecha · Línea · Doble flecha
//   · trazo libre             — Mano alzada · Marcador resaltador
//   · medición                — Línea (m) · Área (m²)
//   · texto                   — nota suelta · nota con recuadro
//   · grosor de trazo         — fino / 8pt / 24pt (sus dos pesos + el de origen)
//
// DECISIÓN CLAVE: óvalo, nube y área NO agregan casos al pintor. Se generan
// como polígonos al momento de dibujar, así que `CapaMarcas` los pinta con el
// mismo `<polygon>` que ya tenía para el recuadro. Un óvalo "de verdad"
// (`<ellipse>`) habría pedido un `EstiloMarca` nuevo, una rama en el pintor y
// un caso en la etiqueta de área: tres lugares para ganar lo mismo.

export type Herramienta =
  | 'mover' | 'calibrar'
  // arrastre de dos puntos
  | 'medir' | 'rect' | 'oval' | 'nube' | 'flecha' | 'flecha2' | 'linea'
  // trazo libre
  | 'libre' | 'marcador' | 'area' | 'medirLibre'
  // un toque
  | 'texto' | 'textoCaja'
  // un toque que NO deja una marca: pincha un salto a otra lámina (F-3)
  | 'vinculo' | 'desvincular'
  // un toque que tampoco deja marca: clava una foto de obra en el punto (F-4)
  | 'foto'
  // un toque que clava una TAREA real de ArquiFlow en el punto (F-8)
  | 'chinche' | 'despinchar';

/** Las de arrastre comparten el gesto: bajar, mover, soltar. */
export const DE_ARRASTRE: Herramienta[] = [
  'rect', 'oval', 'nube', 'flecha', 'flecha2', 'linea', 'calibrar',
];

/** Las de trazo libre acumulan todos los puntos del recorrido del dedo. */
export const DE_TRAZO: Herramienta[] = ['libre', 'marcador', 'medirLibre'];

/**
 * Cada cuántos píxeles de PANTALLA se clava un punto de una medición a mano
 * alzada. Es el dato que la hace confiable, y no se eligió a ojo.
 *
 * Un trazo a mano no se mide como se dibuja. El dedo tiembla, y cada
 * micro-zigzag SUMA: medido sobre un recorrido recto de 400 px muestreado cada
 * 2,5 px —lo que entrega un `pointermove` real—, un temblor de 1 px de
 * desviación devuelve 466 px. Un 16 % de más, siempre hacia arriba. Es la
 * paradoja de la costa, y cuanto más fino se muestrea, PEOR es la medida.
 *
 * Por eso esto no reusa el paso de 2 px del lápiz: a 2 px el error queda igual
 * que crudo (+16 %), a 4 px baja a +6 % y a 8 px a +1,8 %. Más allá de 8 el
 * rendimiento se aplana y se empiezan a perder curvas reales —una pared curva
 * dejaría de ser curva—, así que 8 es donde la medida se endereza sin que el
 * dibujo mienta.
 *
 * Va en píxeles de PANTALLA y no de hoja porque lo que hay que filtrar es el
 * pulso de la mano, que mide lo mismo en la pantalla esté donde esté el zoom.
 */
export const PASO_MEDIDA_PX = 8;

/**
 * Las de VÉRTICES (F-9): se clavan de a un punto y el trazo sobrevive entre
 * gestos. Cierran con doble toque.
 *
 * Son las dos de medición, y eso no es casualidad. Medir es lo único que pide
 * exactitud, y el dedo no la tiene: una mano alzada dibuja un contorno que se
 * PARECE al cuarto, y después su m² se lee como un dato. Con un punto por
 * esquina, cada vértice se mira antes de soltarlo.
 *
 * `calibrar` se queda en `DE_ARRASTRE` a propósito y NO se toca (freno de
 * Daniel, 20-oct): son exactamente dos puntos y ya anda.
 */
export const DE_VERTICES: Herramienta[] = ['medir', 'area'];

/**
 * El alcance del imán ortogonal, en METROS de plano.
 *
 * Es el `IMAN_ORTO` del editor de /planta, copiado con su valor. Va en metros y
 * no en píxeles de pantalla porque la consecuencia —que con mucho zoom agarre
 * desde más lejos en pantalla— es la correcta: con mucho zoom uno está
 * afinando, y es justo cuando quiere que pegue.
 */
export const IMAN_ORTO = 0.12;

/** Lo que el imán agarró, para poder dibujar la guía. `null` = no agarró. */
export type Iman = { x: number | null; y: number | null };

/**
 * Pega el punto a la horizontal o la vertical de sus vecinos, si está cerca.
 *
 * Es `imantarVertice` de `EditorPlanta.tsx`, con los vecinos que tiene un punto
 * que todavía NO existe: el último clavado y el PRIMERO. El primero importa
 * tanto como el último — es contra él que se cierra, y sin su eje la última
 * esquina de un cuarto rectangular nunca cuadra.
 *
 * No hay modo que prender ni tecla que sostener: la DISTANCIA es el
 * interruptor, que es lo que lo hace servible en un celular, donde no existe
 * Shift. Adentro del umbral el punto se acuesta sobre el eje; afuera cae
 * exactamente donde lo llevan.
 *
 * Cada vecino aporta UN SOLO eje —el más cercano de los dos— porque pegarle los
 * dos al mismo vecino sería ponerle el punto encima: el lado se iría a cero.
 */
export function imantarPunto(
  vecinos: Punto[], x: number, y: number, umbral: number,
): { p: Punto; iman: Iman } {
  const iman: Iman = { x: null, y: null };
  let fx = x;
  let fy = y;
  for (const v of vecinos) {
    if (!v) continue;
    const dx = Math.abs(v[0] - x);
    const dy = Math.abs(v[1] - y);
    // El eje más cercano gana: el vecino que está casi en la misma columna
    // quiere un lado vertical, no uno horizontal.
    if (dx <= dy && dx <= umbral && iman.x == null) { fx = v[0]; iman.x = v[0]; }
    else if (dy < dx && dy <= umbral && iman.y == null) { fy = v[1]; iman.y = v[1]; }
  }
  return { p: [fx, fy], iman };
}

/** Cuántos vértices hacen falta para que la figura exista. */
export function minimoVertices(h: Herramienta): number {
  return h === 'area' ? 3 : 2;
}

/** Las que abren un prompt de texto al tocar, sin arrastrar. */
export const DE_TEXTO: Herramienta[] = ['texto', 'textoCaja'];

/** Las que no pintan nada y por lo tanto no piden color ni grosor. */
export const SIN_ESTILO: Herramienta[] = [
  'mover', 'calibrar', 'vinculo', 'desvincular', 'foto', 'chinche', 'despinchar',
];

export const PALETA_COLORES = ['#BB4A4A', '#D85A30', '#F0C802', '#1D9E75', '#0C447C'];

/**
 * El color de lo que se está MIDIENDO —la mira, los vértices, las guías del
 * imán, la lupa—. No es un color de marca guardada: es el instrumento, y por
 * eso no sale de `PALETA_COLORES`.
 *
 * Era el verde de marca (`#ADD010`) y había que cambiarlo por una razón que se
 * mide, no por gusto: contra el papel blanco daba 1,78:1 y contra la trama gris
 * de una losa 1,26:1, cuando el mínimo legible para un elemento gráfico es 3:1.
 * Sobre una losa tramada el verde era, literalmente, invisible.
 *
 * Fucsia y no azul —que era la primera idea— porque el plano YA viene lleno de
 * azul (instalaciones, cotas) y el instrumento tiene que distinguirse del
 * dibujo, no mimetizarse. Es el mismo criterio por el que Bluebeam mide en
 * magenta. Acá da 5,87:1 contra blanco y 4,16:1 contra la trama.
 */
export const COLOR_MEDIDA = '#C2185B';

// Los dos pesos que ofrece Procore (8pt y 24pt) más el fino con el que nació
// este visor. El número es el `strokeWidth` en píxeles de PANTALLA, porque el
// trazo es `non-scaling-stroke`.
export const GROSORES = [
  { valor: 2, etiqueta: 'Fino' },
  { valor: 6, etiqueta: '8pt' },
  { valor: 14, etiqueta: '24pt' },
];

type Punto = [number, number];

/**
 * ¿Esta marca es una MEDIDA (distancia o área) y no un dibujo?
 *
 * Se pregunta en tres lugares porque los datos vienen de tres orígenes: las que
 * dibuja este visor traen `measurement`, las importadas de Procore traen
 * `layerType`, y el `style` lo trae cualquiera de las dos. Un solo campo no
 * alcanza y chequear uno nada más deja medidas clasificadas como dibujo.
 *
 * Vive acá y no en `CapaMarcas` porque lo usan dos cosas distintas: el rótulo
 * (que recalcula los metros) y el filtro de capas de F-5 (que separa «Medidas»
 * de «Marcas»). Con la copia en cada lado, apagar la capa de medidas habría
 * dejado afuera un subconjunto distinto al que se rotula.
 */
export function esMedida(d: MarcaData): boolean {
  const p = d.properties;
  return !!p?.measurement
    || String(p?.layerType || '').startsWith('measurement')
    || String(p?.style || '').startsWith('measurement');
}

// ── Geometría ───────────────────────────────────────────────────────────────

/** Un óvalo inscrito en el rectángulo del arrastre, como polígono de 48 lados. */
export function puntosOvalo(a: Punto, b: Punto): Punto[] {
  const cx = (a[0] + b[0]) / 2;
  const cy = (a[1] + b[1]) / 2;
  const rx = Math.abs(b[0] - a[0]) / 2;
  const ry = Math.abs(b[1] - a[1]) / 2;
  const lados = 48;
  const pts: Punto[] = [];
  for (let i = 0; i < lados; i++) {
    const t = (2 * Math.PI * i) / lados;
    pts.push([cx + rx * Math.cos(t), cy + ry * Math.sin(t)]);
  }
  pts.push(pts[0]);
  return pts;
}

const PASOS_FESTON = 7;

/**
 * Festonea el segmento a→b: lo parte en tramos de ~`2·radio` y reemplaza cada
 * tramo por un semicírculo que bulta hacia la normal. Es lo que convierte un
 * rectángulo en nube de revisión.
 */
function festonear(a: Punto, b: Punto, radioObjetivo: number): Punto[] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const largo = Math.hypot(dx, dy);
  if (largo < 1) return [a];
  const n = Math.max(1, Math.round(largo / (2 * radioObjetivo)));
  const ux = dx / largo;
  const uy = dy / largo;
  // Normal hacia AFUERA para un rectángulo recorrido en el orden de
  // `puntosNube` (horario en pantalla, con Y hacia abajo).
  const nx = uy;
  const ny = -ux;
  const paso = largo / n;
  const r = paso / 2;
  const salida: Punto[] = [];
  for (let i = 0; i < n; i++) {
    const cx = a[0] + ux * paso * (i + 0.5);
    const cy = a[1] + uy * paso * (i + 0.5);
    // t va de π a 0: arranca en el inicio del tramo y termina en su final,
    // pasando por el punto más alto del bulto.
    for (let k = 0; k <= PASOS_FESTON; k++) {
      const t = Math.PI - (Math.PI * k) / PASOS_FESTON;
      salida.push([
        cx + ux * r * Math.cos(t) + nx * r * Math.sin(t),
        cy + uy * r * Math.cos(t) + ny * r * Math.sin(t),
      ]);
    }
  }
  return salida;
}

/** Nube de revisión inscrita en el rectángulo del arrastre. */
export function puntosNube(a: Punto, b: Punto): Punto[] {
  const x0 = Math.min(a[0], b[0]);
  const x1 = Math.max(a[0], b[0]);
  const y0 = Math.min(a[1], b[1]);
  const y1 = Math.max(a[1], b[1]);
  // El bulto se dimensiona con el lado CORTO para que una nube larga y angosta
  // no quede con festones gigantes que se coman su propia altura.
  const radio = Math.max(6, Math.min(x1 - x0, y1 - y0) / 6);
  const esquinas: Punto[] = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
  const pts: Punto[] = [];
  for (let i = 0; i < 4; i++) {
    pts.push(...festonear(esquinas[i], esquinas[(i + 1) % 4], radio));
  }
  pts.push(pts[0]);
  return pts;
}

function rectangulo(a: Punto, b: Punto): Punto[] {
  return [[a[0], a[1]], [b[0], a[1]], [b[0], b[1]], [a[0], b[1]], [a[0], a[1]]];
}

// ── De gesto a Feature ──────────────────────────────────────────────────────

/**
 * Lo que deja un arrastre de dos puntos. `null` = esa herramienta no dibuja
 * (mover, calibrar) y el visor ya la atendió por su cuenta.
 */
export function featureDeArrastre(
  h: Herramienta, a: Punto, b: Punto, color: string, grosor: number,
): MarcaData | null {
  const linea = (estilo: 'arrow' | 'drawing', extra?: Record<string, unknown>): MarcaData => ({
    type: 'Feature',
    properties: { style: estilo, color, width: grosor, ...extra },
    geometry: { type: 'LineString', coordinates: [a, b] },
  });
  const poligono = (pts: Punto[], extra?: Record<string, unknown>): MarcaData => ({
    type: 'Feature',
    properties: { style: 'polygon', color, width: grosor, fill: color, ...extra },
    geometry: { type: 'Polygon', coordinates: [pts] },
  });

  switch (h) {
    case 'flecha': return linea('arrow');
    case 'flecha2': return linea('arrow', { doblePunta: true });
    case 'linea': return linea('drawing');
    // El recuadro sigue viajando como `rectangle` (no `polygon`) para no
    // cambiarle el estilo a las marcas que ya existen en la base.
    case 'rect': return poligono(rectangulo(a, b), { style: 'rectangle' });
    case 'oval': return poligono(puntosOvalo(a, b));
    // La nube de revisión NO se rellena: es un señalamiento, no un área.
    case 'nube': return poligono(puntosNube(a, b), { fill: 'none' });
    default: return null;
  }
}

/** Lo que deja un trazo libre. `pts` ya viene en píxeles de la hoja. */
export function featureDeTrazo(
  h: Herramienta, pts: Punto[], color: string, grosor: number,
): MarcaData | null {
  if (pts.length < 2) return null;
  // La medición a mano alzada sale con la MISMA forma que la de vértices: una
  // `measurementline`. Por eso no costó una línea en el pintor —ya suma todos
  // los tramos de cualquier LineString con `measurement`— ni en el filtro de
  // capas, que la ve como medida porque lo es. La diferencia entre las dos está
  // entera en cómo se juntan los puntos, que es donde tiene que estar.
  if (h === 'medirLibre') {
    return {
      type: 'Feature',
      properties: {
        style: 'measurementline', color, width: grosor,
        measurement: true, fontSize: 18,
      },
      geometry: { type: 'LineString', coordinates: pts },
    };
  }
  return {
    type: 'Feature',
    properties: {
      style: 'drawing', color,
      // El resaltador es el mismo trazo, mucho más grueso y traslúcido: así
      // deja ver el plano debajo, que es todo el punto de un marcador.
      width: h === 'marcador' ? Math.max(grosor * 4, 18) : grosor,
      ...(h === 'marcador' ? { opacidad: 0.35 } : {}),
    },
    geometry: { type: 'LineString', coordinates: pts },
  };
}

/**
 * Lo que deja un trazo de VÉRTICES ya cerrado (F-9). `null` si quedó corto.
 *
 * Las dos salen con la MISMA forma que antes producían el arrastre de dos
 * puntos y el lazo, así que lo guardado no cambia de esquema y las medidas que
 * ya están en la base siguen pintándose igual. Lo único que cambió es cómo se
 * juntan los puntos: una `medir` ahora puede traer más de dos, y `largoEnMetros`
 * del pintor ya las sumaba todas —por eso la polilínea no costó una sola línea
 * en `CapaMarcas`.
 *
 * El VALOR tampoco se guarda acá: lo recalcula el pintor. Por eso recalibrar la
 * hoja corrige todas sus medidas de una sola vez.
 */
export function featureDeVertices(
  h: Herramienta, pts: Punto[], color: string, grosor: number,
): MarcaData | null {
  if (pts.length < minimoVertices(h)) return null;
  if (h === 'area') {
    return {
      type: 'Feature',
      properties: {
        style: 'polygon', color, width: grosor, fill: color,
        measurement: true, fontSize: 18,
      },
      geometry: { type: 'Polygon', coordinates: [[...pts, pts[0]]] },
    };
  }
  return {
    type: 'Feature',
    properties: {
      style: 'measurementline', color, width: grosor,
      measurement: true, fontSize: 18,
    },
    geometry: { type: 'LineString', coordinates: pts },
  };
}

/** Lo que deja un toque con una herramienta de texto. */
export function featureDeTexto(
  h: Herramienta, p: Punto, texto: string, color: string,
): MarcaData {
  return {
    type: 'Feature',
    properties: {
      style: 'text', color, fontSize: 18, description: texto,
      ...(h === 'textoCaja' ? { conRecuadro: true } : {}),
    },
    geometry: { type: 'Point', coordinates: p },
  };
}

// ── Editar una marca ya creada (F-2) ────────────────────────────────────────

/**
 * Corre la marca `dx, dy` píxeles de hoja. Recursiva sobre las coordenadas en
 * vez de un `switch` por tipo de geometría: un GeoJSON es un árbol de pares
 * [x, y] y da igual a qué profundidad estén. Un Point, una LineString, un
 * Polygon y un MultiLineString se mueven con el mismo código, y la herramienta
 * número quince que agreguemos también.
 */
export function trasladar(d: MarcaData, dx: number, dy: number): MarcaData {
  const correr = (c: unknown): unknown => {
    if (!Array.isArray(c)) return c;
    // Un par de números ES un punto: es la hoja del árbol.
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      return [c[0] + dx, c[1] + dy, ...c.slice(2)];
    }
    return c.map(correr);
  };
  return {
    ...d,
    geometry: { ...d.geometry, coordinates: correr(d.geometry.coordinates) as number[] },
  };
}

/**
 * Repinta una marca existente. El relleno solo se cambia si la marca YA tenía
 * uno: la nube de revisión viaja con `fill:'none'` a propósito y re-estilarla
 * no debe convertirla en una mancha de color.
 */
export function reestilar(d: MarcaData, color: string, grosor: number): MarcaData {
  const p = d.properties || {} as MarcaData['properties'];
  return {
    ...d,
    properties: {
      ...p,
      color,
      // El texto no tiene trazo: cambiarle el `width` no se ve y el resaltador
      // tiene el suyo multiplicado, así que tampoco se le pisa a mano.
      ...(p.style === 'text' ? {} : { width: p.opacidad !== undefined ? Math.max(grosor * 4, 18) : grosor }),
      ...(p.fill && p.fill !== 'none' ? { fill: color } : {}),
    },
  };
}

/**
 * Simplifica un trazo libre tirando los puntos que no aportan curva. Un dedo
 * sobre un plano deja cientos de puntos por segundo y todos van al JSON de la
 * marca; con esto una mano alzada de 400 puntos baja a ~60 sin que se note.
 * Criterio por distancia, que para esto rinde igual que Douglas-Peucker y cabe
 * en cinco líneas.
 */
export function adelgazarTrazo(pts: Punto[], minPx: number): Punto[] {
  if (pts.length < 3) return pts;
  const salida: Punto[] = [pts[0]];
  for (const p of pts.slice(1, -1)) {
    const u = salida[salida.length - 1];
    if (Math.hypot(p[0] - u[0], p[1] - u[1]) >= minPx) salida.push(p);
  }
  salida.push(pts[pts.length - 1]);
  return salida;
}
